import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { MaterialModule } from '@sharedModule/material/material.module';
import { ResponseIdentity } from '@sharedModule/models/server-response-identity.model';
import { FormatTextPipe } from '@sharedModule/pipe/format-text/format-text.pipe';
import { jsonValidator } from '@sharedModule/utils/form.utils';
import { forkJoin, Observable, of, Subject, takeUntil } from 'rxjs';
import { AdapterSettingsService } from '../../services/adapter-settings.service';
import {
  AdapterSettingField,
  MasterAdapterSettingsInterface,
  PartnerAdapterSettingsInterface,
  SettingsInterface,
} from '../../types/adapter-settings';
import {
  UpsertPartnerAdapterSettingsAddInterface,
  UpsertPartnerAdapterSettingsPopupInterface,
  UpsertPartnerAdapterSettingsUpdateInterface,
} from '../../types/upsert-adapter-settings';
import {
  coerceSettingValue,
  fieldKind,
  inputTypeFor,
} from '../../utils/adapter-settings.utils';

interface MasterAdapterOption extends MasterAdapterSettingsInterface {
  name_type: string;
}

@Component({
  selector: 'app-upsert-master-partner-adapter-settings',
  standalone: true,
  imports: [
    InputModule,
    CommonModule,
    MaterialModule,
    FormatTextPipe,
    ReactiveFormsModule,
  ],
  templateUrl: './upsert-master-partner-adapter-settings.component.html',
  styleUrl: './upsert-master-partner-adapter-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpsertMasterPartnerAdapterSettingsComponent
  implements OnInit, OnDestroy
{
  public readonly attrConstant = ATTRIBUTES_CONSTANT.settings.adapter;
  upsertAdapterSettingFrom!: FormGroup;
  adapterSettings: MasterAdapterOption[] = [];
  selectedAdapterSettings?: AdapterSettingField[];
  isSubmitLoading!: boolean;
  isDataLoading = false;
  #destroy$ = new Subject<void>();

  constructor(
    private messageHandlerService: MessageHandlerService,
    private adapterSettingsService: AdapterSettingsService,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA)
    public adapterData: UpsertPartnerAdapterSettingsPopupInterface,
    public closeDialogRef: MatDialogRef<UpsertMasterPartnerAdapterSettingsComponent>
  ) {}

  ngOnInit(): void {
    // Both controls start empty: on edit their values arrive with the fetched
    // record, not from the list row that opened the dialog.
    this.upsertAdapterSettingFrom = new FormGroup({
      [this.attrConstant.adapter.key]: new FormControl(
        { value: '', disabled: true },
        [Validators.required]
      ),
      [this.attrConstant.code.key]: new FormControl('', [Validators.required]),
    });
    this.loadDialogData();
  }

  // The catalog drives the adapter select (add) and the field schema (both
  // paths); on edit the record supplies the adapter identity and saved values.
  // buildEditFields() needs both and neither depends on the other, so they are
  // fetched in parallel. of(null) keeps the add path on the same subscription so
  // there is a single place that clears the loading flag and marks for check.
  private loadDialogData(): void {
    const data = this.adapterData;
    const record$: Observable<ResponseIdentity<PartnerAdapterSettingsInterface> | null> =
      data.isUpdate
        ? this.adapterSettingsService.getPartnerAdapterSetting({
            partner_id: data.partner_id,
            id: data.id,
          })
        : of(null);

    this.isDataLoading = true;
    forkJoin({
      catalog: this.adapterSettingsService.listMasterAdapterSettings(),
      record: record$,
    })
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: ({ catalog, record }) => {
          if (catalog.success) {
            this.adapterSettings = catalog.response.map(item => ({
              ...item,
              name_type: `${item.adapter} - ${item.adapter_type}`,
            }));
          }
          // Order matters: applyRecord -> buildEditFields reads adapterSettings.
          if (record?.success && record.response) {
            this.applyRecord(record.response);
          }
          this.isDataLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          // errorInterceptor already surfaces the message; just release the UI.
          this.isDataLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  // Seeds the static controls from the fetched record. `code` is not rendered on
  // the edit path but still carries Validators.required, so it must be patched
  // or the form stays invalid and the Update button silently does nothing.
  private applyRecord(record: PartnerAdapterSettingsInterface): void {
    this.upsertAdapterSettingFrom.patchValue({
      [this.attrConstant.adapter.key]: record.adapter_name,
      [this.attrConstant.code.key]: record.adapter_id,
    });
    this.buildEditFields(record);
  }

  // On edit prefer the catalog schema (preserves password / number / json kinds),
  // matched by adapter_id; fall back to typeof-inference from the saved values.
  private buildEditFields(record: PartnerAdapterSettingsInterface): void {
    const saved = record.adapter_settings ?? {};
    const defs = this.adapterSettings.find(
      a => a.code === record.adapter_id
    )?.settings;
    if (defs?.length) {
      this.selectedAdapterSettings = defs.map(def =>
        this.buildField(def.name, def.data_type, saved[def.name])
      );
    } else {
      this.selectedAdapterSettings = Object.entries(saved).map(
        ([name, value]) => this.buildField(name, typeof value, value)
      );
    }
  }

  onAdapterChange(event: { value: number }): void {
    // Drop the previously selected adapter's dynamic controls so they don't
    // leak into the payload when the user switches adapters.
    (this.selectedAdapterSettings ?? []).forEach(field =>
      this.upsertAdapterSettingFrom.removeControl(field.name)
    );
    const defs: SettingsInterface[] =
      this.adapterSettings.find(i => i.code === event.value)?.settings ?? [];
    this.selectedAdapterSettings = defs.map(def =>
      this.buildField(def.name, def.data_type, '')
    );
  }

  // Builds the setting's control, registers it on the form group, and returns a
  // view model with the render kind / input type precomputed so the template is
  // binding-only (no method calls during change detection).
  private buildField(
    name: string,
    dataType: string,
    rawValue: unknown
  ): AdapterSettingField {
    const control = this.createControl(dataType, rawValue);
    this.upsertAdapterSettingFrom.addControl(name, control);
    return {
      name,
      data_type: dataType,
      kind: fieldKind(dataType),
      inputType: inputTypeFor(dataType),
      control,
    };
  }

  private createControl(dataType: string, rawValue: unknown): FormControl {
    switch (fieldKind(dataType)) {
      case 'boolean':
        // Default false; required is meaningless for a toggle (always valid).
        return new FormControl(rawValue === true || rawValue === 'true');
      case 'json': {
        let value = '';
        if (rawValue !== null && rawValue !== undefined && rawValue !== '')
          value =
            typeof rawValue === 'string'
              ? rawValue
              : JSON.stringify(rawValue, null, 2);
        return new FormControl(value, [Validators.required, jsonValidator]);
      }
      default:
        return new FormControl(rawValue ?? '', [Validators.required]);
    }
  }

  onSubmit(): void {
    if (this.isDataLoading) return;
    if (this.upsertAdapterSettingFrom.valid) {
      this.isSubmitLoading = true;
      // Narrow through a const alias so `data.id` is a number on the edit path.
      const data = this.adapterData;
      if (data.isUpdate) this.updateAdapterSetting(data.id);
      else this.addAdapterSetting();
    }
  }

  // Serialize each dynamic setting to its proper JS type (number/boolean/JSON).
  private buildAdapterSettings(): Record<string, unknown> {
    const formValue = this.upsertAdapterSettingFrom.value;
    const settings: Record<string, unknown> = {};
    (this.selectedAdapterSettings ?? []).forEach(field => {
      settings[field.name] = coerceSettingValue(
        field.data_type,
        formValue[field.name]
      );
    });
    return settings;
  }

  addAdapterSetting(): void {
    const payload: UpsertPartnerAdapterSettingsAddInterface = {
      partner_id: this.adapterData.partner_id,
      adapter_id:
        this.upsertAdapterSettingFrom.value[this.attrConstant.code.key],
      adapter_settings: this.buildAdapterSettings(),
    };
    this.adapterSettingsService
      .addPartnerAdapterSetting(payload)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.closeDialogRef.close({
              success: true,
              partnerId: this.adapterData.partner_id,
            });
            return;
          }
          // Nothing closes the dialog on a business failure, so release the
          // button instead of leaving it disabled for good.
          this.isSubmitLoading = false;
          this.cdr.markForCheck();
        },
        error: error => {
          console.log(error);
          this.closeDialogRef.close({ success: false });
        },
      });
  }

  updateAdapterSetting(id: number): void {
    const payload: UpsertPartnerAdapterSettingsUpdateInterface = {
      partner_id: this.adapterData.partner_id,
      id,
      adapter_settings: this.buildAdapterSettings(),
    };
    this.adapterSettingsService
      .updatePartnerAdapterSetting(payload)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.closeDialogRef.close({
              success: true,
              partnerId: this.adapterData.partner_id,
            });
            return;
          }
          // Nothing closes the dialog on a business failure, so release the
          // button instead of leaving it disabled for good.
          this.isSubmitLoading = false;
          this.cdr.markForCheck();
        },
        error: error => {
          console.log(error);
          this.closeDialogRef.close({ success: false });
        },
      });
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
